import { useState, useEffect, useRef } from "react";
import { busApi, normalizeVehiclesPayload } from "../services/api/busApi";

/**
 * Hook para rastreamento em tempo real de veículos.
 *
 * Fonte única: GTFS-RT (`GET /api/planner/vehicle-positions`) + GTFS estático.
 * A camada operacional Cittati (map-snapshot) foi estacionada — ver
 * `docs/analise-modulo-mapa.md` §6. Por isso não há mais "snapshot primário" nem
 * fallback aninhado: o backoff só cresce em falha real do GTFS-RT (corrige F1/F2).
 *
 * @param {Object} filters - Filtros para buscar veículos
 * @param {string} filters.routeId - ID de uma linha específica
 * @param {string} filters.routeIds - IDs de múltiplas linhas (separado por vírgula)
 * @param {string|number} filters.directionId - Sentido ("0"/"1" ou 0/1), quando suportado
 * @param {string} filters.bounds - Bounding box (minLat,minLng,maxLat,maxLng)
 * @param {number} filters.limit - Limite de veículos
 * @param {number} intervalMs - Intervalo de atualização em ms (padrão: 30s)
 * @param {boolean} enabled - Se o rastreamento está ativo (padrão: true)
 * @returns {Object} { vehicles, loading, error, source, degraded, warning,
 *                     lastSuccessfulAt, operationalTrips, totals, refetch }
 */
export const useVehicleTracking = (
  filters = {},
  intervalMs = 30000,
  enabled = true
) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState(null);
  const [degraded, setDegraded] = useState(false);
  const [warning, setWarning] = useState(null);
  const [lastSuccessfulAt, setLastSuccessfulAt] = useState(null);
  // operationalTrips deixa de existir sem a camada operacional; mantido como []
  // para preservar o contrato com os consumidores (PlannerPage/PartidasLinha).
  const [operationalTrips] = useState([]);
  const [totals, setTotals] = useState(null);
  const pollTimeoutRef = useRef(null);
  const consecutiveFailuresRef = useRef(0);
  const filtersStringified = JSON.stringify(filters);

  const getFriendlyErrorMessage = (err) => {
    const code = err?.response?.data?.code;
    const apiMessage = err?.response?.data?.message;

    if (code === "INVALID_PARAMS") {
      return apiMessage || "Parâmetros inválidos para consulta de veículos.";
    }

    if (err?.code === "ECONNABORTED") {
      return "Tempo limite excedido ao consultar posições dos veículos.";
    }

    if (apiMessage) {
      return apiMessage;
    }

    return "Falha ao atualizar posições dos veículos.";
  };

  const isAbortError = (err) =>
    err?.name === "CanceledError" ||
    err?.code === "ERR_CANCELED" ||
    err?.name === "AbortError";

  // F4: `isCancelled` gateia TODOS os setState; respostas de um filtro/ciclo já
  // descartado (troca de linha, unmount) não sobrescrevem o estado atual.
  const fetchVehicles = async ({ signal, isCancelled } = {}) => {
    const active = () => !isCancelled || !isCancelled();
    try {
      if (active()) {
        setLoading(true);
        setError(null);
      }

      const response = await busApi.getVehiclePositions(
        filters,
        signal ? { signal } : {}
      );
      if (!active()) return false;

      const data = response?.data;
      const normalized = normalizeVehiclesPayload(data);

      setVehicles(normalized);
      setSource("gtfs_rt");
      setDegraded(false);
      setWarning(null);
      setLastSuccessfulAt(Date.now());
      setTotals(
        data && !Array.isArray(data)
          ? { total: data.total ?? normalized.length, filtered: !!data.filtered }
          : { total: normalized.length, filtered: false }
      );
      consecutiveFailuresRef.current = 0;
      return true;
    } catch (err) {
      // Aborto (troca de filtro/unmount) ou ciclo já cancelado: ignora em silêncio.
      if (isAbortError(err) || !active()) return false;

      console.error("Erro ao buscar veículos (GTFS-RT):", err);
      setError(err);
      setVehicles([]);
      setSource(null);
      setDegraded(false);
      setWarning(getFriendlyErrorMessage(err));
      setLastSuccessfulAt(null);
      setTotals(null);
      consecutiveFailuresRef.current += 1;
      return false;
    } finally {
      if (active()) setLoading(false);
    }
  };

  useEffect(() => {
    // Se desabilitado, limpar timer e estado.
    if (!enabled) {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      setVehicles([]);
      setSource(null);
      setDegraded(false);
      setWarning(null);
      setLastSuccessfulAt(null);
      setTotals(null);
      consecutiveFailuresRef.current = 0;
      return;
    }

    let cancelled = false;
    let controller = null;

    const poll = async () => {
      controller = new AbortController();
      await fetchVehicles({
        signal: controller.signal,
        isCancelled: () => cancelled,
      });

      if (cancelled || intervalMs <= 0) return;

      // Backoff só em falha REAL do GTFS-RT (sem snapshot primário, não há
      // mais o bug de punir o fallback — F1/F2).
      const retrySteps = Math.min(consecutiveFailuresRef.current, 4);
      const nextDelay = intervalMs * Math.pow(2, retrySteps);

      pollTimeoutRef.current = setTimeout(poll, nextDelay);
    };

    poll();

    return () => {
      cancelled = true;
      if (controller) controller.abort(); // aborta requisição em voo na troca/unmount
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, filtersStringified]);

  return {
    vehicles,
    loading,
    error,
    source,
    degraded,
    warning,
    lastSuccessfulAt,
    operationalTrips,
    totals,
    refetch: fetchVehicles,
  };
};

export default useVehicleTracking;
